import PropTypes from 'prop-types';

// material-ui
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

export default function MainCard({
  border = true,
  boxShadow,
  children,
  subheader,
  content = true,
  contentSX = {},
  darkTitle,
  divider = true,
  elevation,
  secondary,
  shadow,
  sx = {},
  title,
  codeHighlight = false,
  codeString,
  modal = false,
  ref,
  ...others
}) {
  return (
    <Card
      elevation={elevation || 0}
      sx={(theme) => ({
        position: 'relative',
        border: border ? '1px solid rgba(31, 91, 64, 0.08)' : 'none',
        borderRadius: 3,
        backgroundImage: border ? 'linear-gradient(180deg, #ffffff 0%, #fcfefd 100%)' : 'none',
        boxShadow: boxShadow && !border
          ? shadow || '0 1px 2px rgba(15, 45, 32, 0.04), 0 4px 20px rgba(15, 45, 32, 0.05)'
          : '0 1px 2px rgba(15, 45, 32, 0.04), 0 4px 16px rgba(15, 45, 32, 0.04)',
        transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
        animation: 'fadeInUp 0.4s ease-out both',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(15, 45, 32, 0.07), 0 12px 32px rgba(15, 45, 32, 0.06)',
          borderColor: 'rgba(31, 91, 64, 0.14)'
        },
        ...(codeHighlight && {
          '& pre': { margin: 0, padding: '12px !important', fontFamily: theme.typography.fontFamily, fontSize: '0.75rem' }
        }),
        ...(modal && {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: `calc(100% - 50px)`, sm: 'auto' },
          maxWidth: 768
        }),
        ...(typeof sx === 'function' ? sx(theme) : sx || {})
      })}
      ref={ref}
      {...others}
    >
      {/* card header and action */}
      {!darkTitle && title && (
        <CardHeader
          sx={{
            p: '20px 24px 12px',
          }}
          slotProps={{
            title: {
              variant: darkTitle ? 'h4' : 'subtitle1',
              sx: {
                fontWeight: 600,
                fontSize: '0.9375rem',
                letterSpacing: '-0.01em',
                color: 'text.primary',
              }
            },
            action: { sx: { m: '0px auto', alignSelf: 'center' } }
          }}
          title={title}
          action={secondary}
          subheader={subheader}
        />
      )}

      {/* content & header divider */}
      {title && divider && (
        <Divider sx={{ borderColor: 'rgba(31, 91, 64, 0.06)' }} />
      )}

      {/* card content */}
      {content && (
        <CardContent sx={{ p: '20px 24px', ...contentSX }}>
          {children}
        </CardContent>
      )}
      {!content && children}
    </Card>
  );
}

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  children: PropTypes.node,
  subheader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  content: PropTypes.bool,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.any,
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  codeHighlight: PropTypes.bool,
  codeString: PropTypes.string,
  modal: PropTypes.bool,
  ref: PropTypes.object,
  others: PropTypes.any
};
